import {
    Box,
    Card,
    CardContent,
    Collapse,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {useState} from 'react';
import EditIcon from '@mui/icons-material/Edit';
import {DashboardProject} from '../../../state/types/Dashboard';

const formatCurrency = (cents: number): string => {
    return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
};

interface ProjectRowProps {
    project: DashboardProject;
    onEditRate: () => void;
}

const ProjectRow = (props: ProjectRowProps) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton size={'small'} onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{props.project.customerName}</TableCell>
                <TableCell>{props.project.projectName}</TableCell>
                <TableCell align={'right'}>{props.project.totalHours.toFixed(2)} h</TableCell>
                <TableCell align={'right'}>{formatCurrency(props.project.totalRevenue)}</TableCell>
                <TableCell align={'right'}>{props.project.employees.length}</TableCell>
                <TableCell>
                    <IconButton size={'small'} onClick={props.onEditRate}>
                        <EditIcon fontSize={'small'} />
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout={'auto'} unmountOnExit={true}>
                        <Box sx={{ margin: 1 }}>
                            <Table size={'small'}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Mitarbeiter</TableCell>
                                        <TableCell align={'right'}>Stunden</TableCell>
                                        <TableCell align={'right'}>Stundensatz</TableCell>
                                        <TableCell align={'right'}>Umsatz</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {props.project.employees.map((emp) => (
                                        <TableRow key={emp.userId}>
                                            <TableCell>{emp.name}</TableCell>
                                            <TableCell align={'right'}>{emp.hours.toFixed(2)} h</TableCell>
                                            <TableCell align={'right'}>
                                                {formatCurrency(emp.hourlyRate)}
                                            </TableCell>
                                            <TableCell align={'right'}>
                                                {formatCurrency(emp.revenue)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

interface RevenueByProjectTableProps {
    projects: DashboardProject[];
    onEditRate: (project: DashboardProject) => void;
}

const RevenueByProjectTable = (props: RevenueByProjectTableProps) => {
    return (
        <Card>
            <CardContent>
                <Typography variant={'h6'} sx={{ marginBottom: 2 }}>
                    Umsatz nach Projekt
                </Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width={50} />
                                <TableCell>Kunde</TableCell>
                                <TableCell>Projekt</TableCell>
                                <TableCell align={'right'}>Stunden</TableCell>
                                <TableCell align={'right'}>Umsatz</TableCell>
                                <TableCell align={'right'}>Mitarbeiter</TableCell>
                                <TableCell width={50} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.projects.map((project) => (
                                <ProjectRow
                                    key={project.projectId}
                                    project={project}
                                    onEditRate={() => props.onEditRate(project)}
                                />
                            ))}
                            {props.projects.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align={'center'}>
                                        <Typography color={'text.secondary'}>
                                            Keine aktiven Projekte in diesem Monat
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
};

export default RevenueByProjectTable;
